import { Message } from "src/modules/communication-protobuf";
import AppStatus from "src/constants/AppStatus";
import { ControlledSoundAlerts } from "src/modules/sound-alerts/SoundAlerts";

export const ErrorType = {
  CONNECTION_LOST: "connectionLost",
  BABY_LOST_CONNECTION: "babyLostConnection",
  BABY_LOST_AUDIO: "babyLostAudio",
  BABY_ON_BACKGROUND: "babyOnBackground",
  BABY_INTERUPTED_AUDIO: "babyInteruptedAudio"
};

export default function createErrorManager({
  rpc,
  peerJid,
  messenger,
  onChange,
  synchronizeStations,
  pingService
}) {
  let currentErrorId = null;

  const errors = {
    [ErrorType.CONNECTION_LOST]: {
      id: ErrorType.CONNECTION_LOST,
      priority: 100,
      delay: 10000,
      active: false,
      timeoutId: null
    },
    [ErrorType.BABY_LOST_CONNECTION]: {
      id: ErrorType.BABY_LOST_CONNECTION,
      priority: 90,
      delay: 10000,
      active: false,
      timeoutId: null
    },
    [ErrorType.BABY_ON_BACKGROUND]: {
      id: ErrorType.BABY_ON_BACKGROUND,
      priority: 81,
      delay: 0,
      active: false,
      timeoutId: null
    },
    [ErrorType.BABY_INTERUPTED_AUDIO]: {
      id: ErrorType.BABY_INTERUPTED_AUDIO,
      priority: 70,
      delay: 0,
      active: false,
      timeoutId: null
    },
    [ErrorType.BABY_LOST_AUDIO]: {
      id: ErrorType.BABY_LOST_AUDIO,
      priority: 60,
      delay: 0,
      active: false,
      timeoutId: null
    }
  };

  function getHighestPriorityError() {
    const activeErrors = Object.values(errors).filter((error) => error.active);
    if (activeErrors.length === 0) return null;
    const highestPriorityError = activeErrors.reduce((result, next) => {
      if (result == null) {
        return next;
      }
      return result.priority > next.priority ? result : next;
    });
    return highestPriorityError;
  }
  function updateCurrentError() {
    let newError = getHighestPriorityError();
    let newErrorId = newError ? newError.id : null;

    if (currentErrorId === newErrorId) return;
    const previousErrorId = currentErrorId;

    currentErrorId = newErrorId;

    if (onChange) {
      onChange(newErrorId);
      if (newError && previousErrorId == null)
        ControlledSoundAlerts.playStartSound();
      if (previousErrorId && newError == null)
        ControlledSoundAlerts.playEndSound();
    }
  }
  function toggleErrorState(errorId) {
    errors[errorId].active = !errors[errorId].active;
    updateCurrentError();
  }

  // CONNECTION LOST
  function onAppStatusChange(appStatus) {
    const error = errors[ErrorType.CONNECTION_LOST];
    if (appStatus === AppStatus.STARTED) {
      if (error.active) {
        toggleErrorState(error.id);
      }
      clearTimeout(error.timeoutId);
      error.timeoutId = null;
    }
    if (appStatus === AppStatus.CONNECTION_LOST) {
      if (error.active || error.timeoutId) return;
      error.timeoutId = setTimeout(() => {
        toggleErrorState(error.id);
        error.timeoutId = null;
      }, error.delay);
    }
  }
  //

  // BABY LOST CONNECTION
  pingService.on("ping-pong-timeout", () => {
    const error = errors[ErrorType.BABY_LOST_CONNECTION];
    if (error.active || error.timeoutId) return;
    error.timeoutId = setTimeout(() => {
      toggleErrorState(error.id);
      error.timeoutId = null;
    }, error.delay);
  });
  pingService.on("ping-response", () => {
    const error = errors[ErrorType.BABY_LOST_CONNECTION];
    clearTimeout(error.timeoutId);
    error.timeoutId = null;
    if (error.active) {
      toggleErrorState(error.id);
    }
  });
  pingService.start({ peerJid, rpc });
  //

  // BABY ON BACKGROUND
  function handleAppStateMessage({ state }, from) {
    const error = errors[ErrorType.BABY_ON_BACKGROUND];
    if (state === "FOREGROUND") {
      clearTimeout(error.timeoutId);
      error.timeoutId = null;
      if (error.active) {
        toggleErrorState(error.id);
      }
    }
    if (state === "BACKGROUND") {
      if (error.active || error.timeoutId) return;
      error.timeoutId = setTimeout(() => {
        toggleErrorState(error.id);
        error.timeoutId = null;
      }, error.delay);
    }
  }
  messenger.on(peerJid, Message.AppStateMessage.name, handleAppStateMessage);
  //

  // BABY LOST AUDIO
  function handleBabyStationAudioStateMessage(
    { state, interruptionReason },
    from
  ) {
    const error =
      interruptionReason === "BUILT_IN_MIC_MUTED"
        ? errors[ErrorType.BABY_INTERUPTED_AUDIO]
        : errors[ErrorType.BABY_LOST_AUDIO];
    if (state === "AUDIO_ON_AGAIN") {
      clearTimeout(error.timeoutId);
      error.timeoutId = null;
      if (error.active) {
        toggleErrorState(error.id);
      }
    }
    if (state === "AUDIO_LOST_TEMPORARLY") {
      if (error.active || error.timeoutId) return;
      error.timeoutId = setTimeout(() => {
        toggleErrorState(error.id);
        error.timeoutId = null;
      }, error.delay);
    }
  }
  messenger.on(
    peerJid,
    Message.BabyStationAudioStateMessage.name,
    handleBabyStationAudioStateMessage
  );
  //

  function destroy() {
    onChange = null;
    messenger.removeListener(
      peerJid,
      Message.AppStateMessage.name,
      handleAppStateMessage
    );
    messenger.removeListener(
      peerJid,
      Message.BabyStationAudioStateMessage.name,
      handleBabyStationAudioStateMessage
    );
  }

  return { destroy, onAppStatusChange, pingService };
}
